/**
 * Welham & Co's cart actions (session W5, plan section 3.4), for the drawer
 * and /cart. Loaded from snippets/wc-body-end-w5 on every page.
 *
 * Three jobs, each ending in the contract Horizon's cart listens for: ONE
 * CartLinesUpdateEvent carrying a PROMISE, resolved with the Section Rendering
 * API HTML of every <cart-items-component> on the page. Horizon's drawer
 * ignores a lines-update event that carries no promise
 * (component-cart-items.js #handleCartUpdate reads `event.promise?.then`), so
 * an event without one leaves the drawer showing the old cart.
 *
 * 1. Removing a portrait removes its brief's extras in the same request
 *    (`[data-wc-remove]` holds every key), so an extra person or a photo
 *    combination is never left charging for a portrait that is gone.
 * 2. The one drawer offer (`[data-wc-offer-add]`) adds with the portrait's
 *    _brief_id, so it groups under the portrait it was offered for.
 * 3. A lines-update event from somebody else that carries NO promise is
 *    answered by re-rendering the drawer, once, a moment later, instead of
 *    being ignored. Shopify's CartLinesUpdateEvent cannot be constructed
 *    without one (its constructor reads promise.then), so this is an app's
 *    or a script's bare `shopify:cart:lines-update` event.
 * 4. A drawer whose content lands late is measured again, so Horizon's
 *    pinned summary is never left with the verdict it gave the empty drawer,
 *    and its scroll fades are repainted, so a line below the fold is signalled.
 */
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from "@shopify/events";
import { fetchConfig } from "@theme/utilities";
import { sectionRenderer } from "@theme/section-renderer";
import { CartSectionRestoredEvent } from "@theme/events";
// Defines <scroll-hint>, which draws the drawer's "more below" fade. Horizon
// only PRELOADS scrolling.js; it runs when a section imports it (slideshow,
// zoom dialog, collection links, filters), so a page without one of those had
// a drawer whose list could overflow with no fade at all.
import "@theme/scrolling";

/**
 * W4's cart property keys (plan section 3.3), provisional until Dani's L-06.
 * A rename is a change here and in snippets/wc-cart-state and
 * snippets/wc-cart-line-brief, nowhere else.
 */
export const KEYS = Object.freeze({
  briefId: "_brief_id",
});

/**
 * Whether the offer joins the portrait's Downpay plan. It does, like the
 * brief's own extras ("Extras join $0 Upfront" is W4's default, matching
 * today's orders), so a $0 cart stays $0. L-10 (Dani and Luke: "do extras join
 * the Downpay plan?") may reverse both; this is the one line to change here.
 * snippets/wc-drawer-offer only offers the plan when the variant carries it.
 */
const OFFER_JOINS_PLAN = true;

const DRAWER_SECTION = "wc-cart-drawer";

/** @returns {string[]} The section id of every cart on the page. */
function cartSectionIds() {
  const ids = new Set();
  for (const el of document.querySelectorAll("cart-items-component[data-section-id]")) {
    const id = /** @type {HTMLElement} */ (el).dataset.sectionId;
    if (id) ids.add(id);
  }
  return [...ids];
}

/**
 * @param {string} message
 * @param {HTMLElement} source
 */
function reportError(message, source) {
  source.dispatchEvent(new CartErrorEvent({ error: message, code: "SERVICE_UNAVAILABLE" }));
}

/**
 * Posts one cart mutation and dispatches the one event Horizon's cart
 * understands. The event is dispatched BEFORE the fetch, as Horizon's own
 * callers do, so listeners can show progress, and it bubbles from `source`,
 * which is never the <cart-items-component> itself (that one skips its own
 * events).
 *
 * @param {object} options
 * @param {string} options.url
 * @param {Record<string, unknown>} options.body
 * @param {'add' | 'remove' | 'update'} options.action
 * @param {Array<{id?: string, merchandiseId?: string, quantity: number}>} options.lines
 * @param {HTMLElement} options.source
 * @returns {Promise<any>} The cart JSON after the change.
 */
async function mutate({ url, body, action, lines, source }) {
  const deferred = CartLinesUpdateEvent.createPromise();
  const sections = cartSectionIds();

  source.dispatchEvent(
    new CartLinesUpdateEvent(
      /** @type {any} */ ({
        action,
        context: source.closest("dialog") ? "dialog" : "cart",
        lines,
        promise: deferred.promise,
      }),
    ),
  );

  try {
    const response = await fetch(
      url,
      fetchConfig("json", {
        body: JSON.stringify({ ...body, sections: sections.join(","), sections_url: window.location.pathname }),
      }),
    );
    const data = await response.json();
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || `Cart request failed (${response.status})`);
    }

    // /cart/add.js answers with the added lines only; the icon and anything
    // else reading `cart` need the whole cart.
    const cart = action === "add" ? await (await fetch(`${Theme.routes.cart_url}.js`)).json() : data;

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: data.sections ?? undefined,
        items: cart.items,
        itemCount: cart.item_count,
        source: "wc-cart",
        didError: false,
      },
    });
    return cart;
  } catch (error) {
    deferred.reject(error);
    reportError(error instanceof Error ? error.message : String(error), source);
    throw error;
  }
}

/** @param {HTMLButtonElement} button */
async function removeGroup(button) {
  const keys = (button.dataset.wcRemove || "").split(",").filter(Boolean);
  if (!keys.length || button.getAttribute("aria-busy") === "true") return;

  button.setAttribute("aria-busy", "true");
  // Disabled while the request is in flight, as Horizon's own removals are.
  // No exit animation: the morph Horizon runs when the promise resolves takes
  // the rows out before this function resumes, so there is nothing left to
  // animate, and the only exit on offer (Horizon's .removing) animates height.
  for (const key of keys) {
    document.getElementById(`CartItem-${key}`)?.closest("cart-items-component")?.classList.add("cart-items-disabled");
  }

  try {
    await mutate({
      url: `${Theme.routes.cart_update_url}.js`.replace(".js.js", ".js"),
      body: { updates: Object.fromEntries(keys.map((key) => [key, 0])) },
      action: "remove",
      lines: keys.map((id) => ({ id, quantity: 0 })),
      source: button,
    });
  } catch {
    // reported through CartErrorEvent; the rows stay, and so does the cart
  } finally {
    button.removeAttribute("aria-busy");
    for (const el of document.querySelectorAll("cart-items-component.cart-items-disabled")) {
      el.classList.remove("cart-items-disabled");
    }
  }
}

/** @param {HTMLButtonElement} button */
async function addOffer(button) {
  if (button.getAttribute("aria-busy") === "true") return;
  const variantId = Number(button.dataset.variantId);
  if (!variantId) return;

  const offer = button.closest(".wc-drawer-offer");
  const status = offer?.querySelector("[data-wc-offer-status]");
  // Read as text from elements Liquid rendered (snippets/wc-body-end-w5): the
  // HTML parser has already decoded `t`'s escaping, once.
  const strings = Object.fromEntries(
    [...document.querySelectorAll("#WcCartStrings [data-key]")].map((el) => [
      /** @type {HTMLElement} */ (el).dataset.key,
      el.textContent?.trim() || "",
    ]),
  );

  /** @type {Record<string, unknown>} */
  const item = { id: variantId, quantity: 1 };
  if (button.dataset.briefId) item.properties = { [KEYS.briefId]: button.dataset.briefId };
  if (OFFER_JOINS_PLAN && button.dataset.portraitPlan) item.selling_plan = Number(button.dataset.portraitPlan);

  button.setAttribute("aria-busy", "true");
  try {
    await mutate({
      url: Theme.routes.cart_add_url.endsWith(".js") ? Theme.routes.cart_add_url : `${Theme.routes.cart_add_url}.js`,
      body: { items: [item] },
      action: "add",
      lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity: 1 }],
      source: button,
    });
    if (status) status.textContent = strings.added || "";
  } catch {
    if (status) status.textContent = strings.failed || "";
  } finally {
    button.removeAttribute("aria-busy");
  }
}

/**
 * A portrait's Remove asks first (snippets/wc-cart-line): the brief behind it
 * is the customer's own words and is not saved anywhere else once added.
 * "Keep it" takes focus, so Enter on a mis-tap keeps the portrait.
 *
 * @param {HTMLButtonElement} ask
 * @param {boolean} open
 */
function toggleConfirm(ask, open) {
  const panel = document.getElementById(ask.getAttribute("aria-controls") || "");
  if (!panel) return;
  ask.setAttribute("aria-expanded", String(open));
  ask.hidden = open;
  panel.hidden = !open;
  const focusTarget = open ? panel.querySelector("[data-wc-remove-keep]") : ask;
  if (focusTarget instanceof HTMLElement) focusTarget.focus();
}

document.addEventListener("keydown", (event) => {
  if (event.key !== "Escape") return;
  const panel = event.target instanceof Element ? event.target.closest(".wc-cart-line__confirm") : null;
  if (!panel || panel.hidden) return;
  // Close the question, not the drawer: <theme-drawer> leaves an Escape a
  // nested component has already handled.
  event.preventDefault();
  const ask = document.querySelector(`[aria-controls="${panel.id}"]`);
  if (ask instanceof HTMLButtonElement) toggleConfirm(ask, false);
});

document.addEventListener("click", (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const ask = target?.closest("[data-wc-remove-ask]");
  if (ask instanceof HTMLButtonElement) {
    event.preventDefault();
    toggleConfirm(ask, true);
    return;
  }
  const keep = target?.closest("[data-wc-remove-keep]");
  if (keep) {
    const panel = keep.closest(".wc-cart-line__confirm");
    const back = panel && document.querySelector(`[aria-controls="${panel.id}"]`);
    if (back instanceof HTMLButtonElement) toggleConfirm(back, false);
    return;
  }
  const remove = target?.closest("[data-wc-remove]");
  if (remove instanceof HTMLButtonElement) {
    event.preventDefault();
    removeGroup(remove);
    return;
  }
  const add = target?.closest("[data-wc-offer-add]");
  if (add instanceof HTMLButtonElement) {
    event.preventDefault();
    addOffer(add);
  }
});

/* ---- 3. An event without a promise still refreshes the cart ------------- */

let refreshTimer = 0;

/** @param {Event} event */
function onLinesUpdate(event) {
  if (/** @type {any} */ (event).promise) return;
  window.clearTimeout(refreshTimer);
  // A caller that forgot the promise has usually not finished its fetch
  // either; give it a moment, then ask Shopify what the cart really is.
  refreshTimer = window.setTimeout(() => {
    for (const id of cartSectionIds()) {
      sectionRenderer
        .renderSection(id, { cache: false, mode: id === DRAWER_SECTION ? "hydration" : "full" })
        .catch((error) => {
          if (error?.name !== "AbortError")
            console.warn("[wc-cart] refresh after a promise-less update failed:", error);
        });
    }
  }, 400);
}

document.addEventListener(StandardEvents.cartLinesUpdate, onLinesUpdate);

/* ---- 4. A drawer re-rendered late is measured again --------------------- */

/*
 * Horizon pins the drawer's summary by measuring it (cart-drawer.js
 * #updateStickyState) when the drawer opens and one frame after a cart event's
 * promise resolves. Content that lands after that frame, from a section
 * fetched late or a hydration morph, keeps the verdict Horizon gave the drawer
 * before it. For an empty drawer that verdict is "no summary", so
 * cart-summary-sticky="false". Live on 2026-09-25 (04:35Z) the summary was
 * 0.385 of the drawer and still unpinned, with "Check out" below the fold.
 *
 * So measure again through Horizon's own entry point for a re-render,
 * cart-section:restored, whenever the drawer's content or size changes (the
 * size moves with Safari's bars, which change 100dvh). At most once per frame,
 * and only while the drawer is open, because Horizon measures on open. It
 * cannot answer itself: the measurement writes one attribute on the <dialog>,
 * which is outside what the MutationObserver watches and does not resize it.
 */
function watchDrawerMeasure() {
  const drawer = document.querySelector("cart-drawer-component");
  const dialog = drawer?.closest("dialog");
  if (!drawer || !dialog) return;
  let frame = 0;
  const measureAgain = () => {
    if (frame || !dialog.open) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      if (!dialog.open) return;
      drawer.dispatchEvent(new CartSectionRestoredEvent());
      // Horizon's <scroll-hint> repaints its "more below" fade only on scroll or
      // when its OWN box resizes, and the drawer's list has a fixed box. Live on
      // 15 Oct a brief's Digital copy line (and the offer under it) landed below
      // the fold after the drawer opened, with no fade, so the total included a
      // line the shopper could not see. A synthetic scroll runs Horizon's own
      // repaint; it moves nothing, and the K8-03 rule still keeps the outer box
      // free of a fade while the summary is pinned.
      for (const hint of drawer.querySelectorAll("scroll-hint")) hint.dispatchEvent(new Event("scroll"));
    });
  };
  new MutationObserver(measureAgain).observe(drawer, { childList: true, subtree: true });
  new ResizeObserver(measureAgain).observe(dialog);

  // Horizon re-renders the drawer by MORPHING it to the server's HTML, which
  // carries no inline style, so a render of the same lines strips the fade
  // ScrollHint wrote and changes no child (live, 15 Oct 20:12: no fade over a
  // hidden line). Only a list that overflows and has LOST its fade is repainted,
  // so ScrollHint's own write cannot wake this again.
  let fadeFrame = 0;
  new MutationObserver((records) => {
    if (
      fadeFrame ||
      !dialog.open ||
      !records.some((r) => r.target instanceof HTMLElement && r.target.localName === "scroll-hint")
    )
      return;
    fadeFrame = requestAnimationFrame(() => {
      fadeFrame = 0;
      for (const hint of drawer.querySelectorAll("scroll-hint")) {
        if (!(hint instanceof HTMLElement) || hint.style.maskImage) continue;
        if (hint.scrollHeight > hint.clientHeight + 1) hint.dispatchEvent(new Event("scroll"));
      }
    });
  }).observe(drawer, { attributes: true, attributeFilter: ["style"], subtree: true });
}

watchDrawerMeasure();
